import { Controller, UseControllerProps } from 'react-hook-form';
import { TextInput, Props } from '../Inputs/TextInput';
import { RequiredField } from './types';
import { useRules } from './hooks';

/**
 * TextFieldProps
 * @public
 */
export type TextFieldProps = Props & UseControllerProps & RequiredField;

/**
 * TextField component
 * @public
 *
 * @example
 * ```tsx
 * <Form>
 *  <TextField name="field.name" required />
 * </Form>
 * ```
 *
 * @param rules - validation rules
 * @param shouldUnregister - unregister field when unmount
 * @param defaultValue - default value
 * @param control - form control
 * @param required - is field required
 * @param restProps - props
 *
 */
export const TextField: React.FC<TextFieldProps> = ({
  rules,
  shouldUnregister,
  defaultValue,
  control,
  required,
  ...restProps
}) => {
  const innerRules = useRules({ required, ...rules });
  const { name } = restProps;

  return (
    <Controller
      rules={innerRules}
      shouldUnregister={shouldUnregister}
      defaultValue={defaultValue}
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const { ref, value, ...fieldProps } = field;

        return (
          <TextInput
            ref={ref}
            value={value || ''}
            error={fieldState.error?.message}
            {...fieldProps}
            {...restProps}
          />
        );
      }}
    />
  );
};
